import { CallState, type PriceRange } from "@b2bportal/core-types";
import type {
  ActivityAvailability,
  ActivityTagCount,
  AvailabilitySort,
  DurationRefinement,
  StartTimeRefinement,
} from "@b2bportal/rbc-activities-api";
import { type PayloadAction, createSlice } from "@reduxjs/toolkit";
import type {
  ActivityLocationSuggestion,
  ActivityRatingValueEnum,
} from "@tenant/activities";
import {
  initialActivitiesAvailabilityState,
  initialFiltersState,
} from "./initialState";
import { fetchActivities } from "./thunks/fetchActivities";
import { fetchActivitiesNextPage } from "./thunks/fetchActivitiesNextPage";
import { initializeSearchState } from "./thunks/initializeActivities";
import type {
  ActivitiesAvailabilityState,
  ActivitiesAvailabilitySearchState,
  ActivityFilterState,
  ActivitySearchDetails,
  SearchId,
  DateRange,
} from "./types";

// Payload types for fetch actions
interface FetchActivitiesInitialPayload {
  activities: ActivityAvailability[];
  categoryTags?: ActivityTagCount[];
  popularFeatureTags?: ActivityTagCount[];
  nextPageToken?: string | null;
  searchId: string;
  totalCount?: number;
}

interface FetchActivitiesMorePayload {
  activities: ActivityAvailability[];
  nextPageToken?: string | null;
  searchId: string;
}

// Handler for initial fetch fulfilled
const handleFetchInitialFulfilled = (
  state: ActivitiesAvailabilityState,
  action: PayloadAction<FetchActivitiesInitialPayload & SearchId>
) => {
  state.activities = {
    state: CallState.Success,
    data: {
      items: action.payload.activities,
      searchId: action.payload.searchId,
      nextPageToken: action.payload.nextPageToken ?? null,
      totalCount: action.payload.totalCount,
      categoryTags: action.payload.categoryTags ?? [],
      popularFeatureTags: action.payload.popularFeatureTags ?? [],
    },
  };
  state.initialized = true;
};

// Handler for next page fetch fulfilled
const handleFetchNextPageFulfilled = (
  state: ActivitiesAvailabilityState,
  action: PayloadAction<FetchActivitiesMorePayload & SearchId>
) => {
  state.activities =
    state.activities.data?.searchId === action.payload.searchId
      ? {
          state: CallState.Success,
          data: {
            items:
              state.activities.data?.items != null
                ? [...state.activities.data.items, ...action.payload.activities]
                : action.payload.activities,
            nextPageToken: action.payload.nextPageToken ?? null,
            searchId: action.payload.searchId,
            totalCount: state.activities.data.totalCount,
            categoryTags: state.activities.data?.categoryTags ?? [],
            popularFeatureTags: state.activities.data?.popularFeatureTags ?? [],
          },
        }
      : state.activities;
};

// Handler for fetch pending
const handleFetchInitialPending = (state: ActivitiesAvailabilityState) => {
  state.search = state.requestedSearch;
  state.activities = {
    state: CallState.InProcess,
    data: {
      items: [],
      nextPageToken: null,
      searchId: state.activities.data?.searchId ?? null,
      categoryTags: state.activities.data?.categoryTags ?? [],
      popularFeatureTags: state.activities.data?.popularFeatureTags ?? [],
    },
  };
  state.initialized = false;
};

// Handler for fetch rejected
const handleFetchInitialRejected = (
  state: ActivitiesAvailabilityState,
  action: PayloadAction<{ error: string } | undefined>
) => {
  state.activities = {
    state: CallState.Failed,
    data: {
      items: [],
      nextPageToken: null,
      searchId: null,
      categoryTags: [],
      popularFeatureTags: [],
    },
    error: action.payload?.error ?? "Error undefined",
  };
};

// Handler for next page pending
const handleFetchNextPagePending = (state: ActivitiesAvailabilityState) => {
  state.activities.state = CallState.InProcess;
};

// Handler for next page rejected
const handleFetchNextPageRejected = (
  state: ActivitiesAvailabilityState,
  action: PayloadAction<{ error: string } | undefined>
) => {
  state.activities = {
    data: state.activities.data,
    state: CallState.Failed,
    error: action.payload?.error ?? "Error undefined",
  };
};

export const activitiesAvailabilitySlice = createSlice({
  name: "activitiesAvailability",
  initialState: initialActivitiesAvailabilityState,
  reducers: {
    // Search form actions
    setNewPlace: (
      state,
      action: PayloadAction<ActivityLocationSuggestion | null>
    ) => {
      state.searchFormValues.place = action.payload || undefined;
    },
    setNewDateFrom: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        from: action.payload,
      };
    },
    setNewDateUntil: (state, action: PayloadAction<string | undefined>) => {
      state.searchFormValues.dateRange = {
        ...state.searchFormValues.dateRange,
        until: action.payload,
      };
    },
    setNewDateRange: (state, action: PayloadAction<DateRange>) => {
      state.searchFormValues.dateRange = action.payload;
    },
    setNewTravelers: (state, action: PayloadAction<number | undefined>) => {
      state.searchFormValues.travelers = action.payload;
    },
    setRequestedSearch: (
      state,
      action: PayloadAction<ActivitySearchDetails>
    ) => {
      state.requestedSearch = action.payload;
    },

    // Filter actions
    resetFilters(state) {
      state.filters = initialFiltersState;
    },
    setAllFilters(state, action: PayloadAction<ActivityFilterState>) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    setPriceFilter(state, action: PayloadAction<PriceRange>) {
      state.filters.maxPrice = action.payload.max;
    },
    setCancellationFilter(state, action: PayloadAction<boolean>) {
      state.filters.freeCancellation = action.payload;
    },
    setCategoriesFilter(state, action: PayloadAction<string[]>) {
      state.filters.categories = action.payload;
    },
    setRatingFilter(state, action: PayloadAction<ActivityRatingValueEnum>) {
      state.filters.rating = action.payload;
    },
    setStartTimeFilter(state, action: PayloadAction<StartTimeRefinement[]>) {
      state.filters.startTime = action.payload;
    },
    setDurationFilter(state, action: PayloadAction<DurationRefinement[]>) {
      state.filters.duration = action.payload;
    },
    setPopularFeatureTagsFilter(state, action: PayloadAction<string[]>) {
      state.filters.popularFeatureTags = action.payload;
    },
    setUserRatingFilter(state, action: PayloadAction<number>) {
      state.filters.userRating = action.payload;
    },
    resetStarRatingFilter(state) {
      state.filters.rating = initialFiltersState.rating;
    },

    // Sort action
    setSort: (state, action: PayloadAction<AvailabilitySort>) => {
      state.sort = action.payload;
    },

    // Reset actions
    resetRatingFilter(state) {
      state.filters.rating = initialFiltersState.rating;
    },
    resetCancellationFilter(state) {
      state.filters.freeCancellation = initialFiltersState.freeCancellation;
    },
    resetCategoriesFilter(state) {
      state.filters.categories = initialFiltersState.categories;
    },
    resetDurationFilter(state) {
      state.filters.duration = initialFiltersState.duration;
    },
    resetPopularFeatureTagsFilter(state) {
      state.filters.popularFeatureTags = initialFiltersState.popularFeatureTags;
    },

    // Fetch handlers (can be used directly or with thunks)
    handleFetchInitialPending,
    handleFetchInitialRejected,
    handleFetchInitialFulfilled,
    handleFetchNextPagePending,
    handleFetchNextPageRejected,
    handleFetchNextPageFulfilled,
  },
  extraReducers: (builder) => {
    builder
      // Initialize search state from URL
      .addCase(
        initializeSearchState.fulfilled,
        (state, action: PayloadAction<ActivitiesAvailabilitySearchState>) => {
          state.searchFormValues = action.payload.searchFormValues;
          state.requestedSearch = action.payload.requestedSearch;
          state.sort = action.payload.sort;
          state.filters = action.payload.filters;
          state.initialized = true;
        }
      )
      // Fetch initial activities
      .addCase(fetchActivities.pending, handleFetchInitialPending)
      .addCase(fetchActivities.fulfilled, handleFetchInitialFulfilled)
      .addCase(fetchActivities.rejected, handleFetchInitialRejected)
      // Fetch next page
      .addCase(fetchActivitiesNextPage.pending, handleFetchNextPagePending)
      .addCase(fetchActivitiesNextPage.fulfilled, handleFetchNextPageFulfilled)
      .addCase(fetchActivitiesNextPage.rejected, handleFetchNextPageRejected);
  },
});

export type ActivitiesAvailabilityActionTypes = ReturnType<
  (typeof ActivitiesAvailabilityActions)[keyof typeof ActivitiesAvailabilityActions]
>;

export const ActivitiesAvailabilityActions =
  activitiesAvailabilitySlice.actions;

export default activitiesAvailabilitySlice.reducer;
